// Block-2-Karte „Meine Kunden" (AP-K, DD11/DD8): KPI „N Kunden" + Top-5 der
// Kunden des gewählten Scope, sortiert nach letzter Aktivität (DD11).
// Pills „Meine"/„Team" (DD8). Für gfgl zusätzlich zwei Auswahl-Filter (P6):
// „BO-Team" und „FO-Verantwortlich" (Popover), die die Kundenliste über die
// bestehende contact-Filter-Mechanik (boTeamIds/foUserIds) einschränken. Bei
// aktivem gfgl-Filter wird mandantenweit (scope=all) statt own/team geladen,
// damit ein fremdes Team / eine fremde FO-Person nicht durch den Scope fällt.
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { People20Regular, ChevronRight16Regular, ChevronDown16Regular } from "@fluentui/react-icons";
import { Popover, PopoverTrigger, PopoverSurface, Checkbox } from "@fluentui/react-components";
import { dashboardService, type DashScope, type DashboardContact } from "../../services/dashboardService";
import { useAuth } from "../../context/AuthContext";
import { fetchTeamsLite } from "../../utils/teamsLite";
import { listAppUsers } from "../../services/userService";
import { contactTypeLabel } from "../../utils/contactTypeLabel";
import { REFINED } from "../risikoanalyseStep/tokens";
import { DashCard, EmptyHint, FilterPill, InitialsAvatar, deDate } from "./DashboardShared";

const LIMIT = 5;

/** Option für die gfgl-Auswahl-Filter (BO-Team / FO-Verantwortlich). */
type FilterOption = { id: string; label: string };

export const MeineKundenCard: React.FC = () => {
    const navigate = useNavigate();
    const { isGfgl } = useAuth();
    const [scope, setScope] = useState<DashScope>("own");
    const [items, setItems] = useState<DashboardContact[]>([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);

    // gfgl-Filter (P6): Optionen + Auswahl.
    const [teamOptions, setTeamOptions] = useState<FilterOption[]>([]);
    const [userOptions, setUserOptions] = useState<FilterOption[]>([]);
    const [boTeamIds, setBoTeamIds] = useState<string[]>([]);
    const [foUserIds, setFoUserIds] = useState<string[]>([]);

    // BO-Teams + FO-User nur für gfgl laden (gleiche Quellen wie die Kontakte-Tabelle).
    useEffect(() => {
        if (!isGfgl) return;
        let alive = true;
        (async () => {
            try {
                const teams = await fetchTeamsLite();
                if (alive) {
                    setTeamOptions(
                        teams
                            .map((t: any) => ({ id: String(t.id), label: String(t.name ?? "").replace(/^Team\s+/i, "") }))
                            .filter((o) => o.id && o.label),
                    );
                }
            } catch (e) {
                console.error("teams-lite laden fehlgeschlagen", e);
            }
            try {
                const users = await listAppUsers();
                if (alive) {
                    setUserOptions(
                        users
                            // FO = Front-Office (department enthält „FO"), Admins ausgenommen.
                            .filter((u) => (u.department ?? "").split("+").includes("FO") && (u.role ?? "").toLowerCase() !== "admin")
                            .map((u) => ({
                                id: String(u.id),
                                label: [u.lastName, u.firstName].filter(Boolean).join(", ") || u.name || u.email || String(u.id),
                            }))
                            .filter((o) => o.id && o.label),
                    );
                }
            } catch (e) {
                console.error("users laden fehlgeschlagen", e);
            }
        })();
        return () => {
            alive = false;
        };
    }, [isGfgl]);

    // Bei aktivem gfgl-Filter mandantenweit laden (scope=all), sonst own/team.
    const hasGfglFilter = isGfgl && (boTeamIds.length > 0 || foUserIds.length > 0);
    const effectiveScope: DashScope | "all" = hasGfglFilter ? "all" : scope;

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        dashboardService
            .getMyContacts(effectiveScope, LIMIT, { boTeamIds, foUserIds })
            .then((res) => {
                if (cancelled) return;
                setItems(res.items);
                setCount(res.count);
            })
            .catch(() => {
                if (cancelled) return;
                setItems([]);
                setCount(0);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [effectiveScope, boTeamIds, foUserIds]);

    const countLabel = useMemo(() => (count === 1 ? "Kunde" : "Kunden"), [count]);

    const goAll = () => navigate("/contacts");
    const goContact = (id: number) => navigate(`/contacts/${id}`);

    return (
        <DashCard
            title="Meine Kunden"
            icon={<People20Regular />}
            right={
                <button
                    type="button"
                    onClick={goAll}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        color: REFINED.aub,
                        fontFamily: REFINED.fontUI,
                        fontSize: "0.88rem",
                        fontWeight: 700,
                        padding: 0,
                    }}
                >
                    Alle Kontakte <ChevronRight16Regular />
                </button>
            }
        >
            {/* Pills Meine/Team (DD8) + gfgl-Auswahl-Filter BO-Team/FO-Verantwortlich (P6) */}
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, paddingTop: 8, paddingBottom: 4 }}>
                <FilterPill label="Meine" count={scope === "own" ? count : 0} active={scope === "own"} onClick={() => setScope("own")} />
                <FilterPill label="Team" count={scope === "team" ? count : 0} active={scope === "team"} onClick={() => setScope("team")} />
                {isGfgl && (
                    <>
                        <FilterSelect label="BO-Team" options={teamOptions} selected={boTeamIds} onChange={setBoTeamIds} />
                        <FilterSelect label="FO-Verantwortlich" options={userOptions} selected={foUserIds} onChange={setFoUserIds} />
                    </>
                )}
            </div>

            {/* KPI: N Kunden */}
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "6px 6px 10px" }}>
                <span style={{ fontSize: "2rem", fontWeight: 800, color: REFINED.ink, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                    {count}
                </span>
                <span style={{ fontSize: "0.92rem", color: REFINED.ink2, fontWeight: 600 }}>{countLabel}</span>
            </div>

            {/* Top-5-Liste: Kunde (Avatar + Name) + Typ + letzte Aktivität (DD11) */}
            {loading ? (
                <EmptyHint>Lade Kunden…</EmptyHint>
            ) : items.length === 0 ? (
                <EmptyHint>
                    {hasGfglFilter ? "Keine Kunden für diesen Filter." : scope === "own" ? "Keine Kunden." : "Keine Kunden im Team."}
                </EmptyHint>
            ) : (
                <div>
                    {items.map((c, idx) => (
                        <ContactRow key={c.id} contact={c} border={idx > 0} onClick={() => goContact(c.id)} />
                    ))}
                </div>
            )}
        </DashCard>
    );
};

/* ────────── gfgl-Auswahl-Filter als Popover (Mehrfachauswahl, P6) ────────── */

const FilterSelect: React.FC<{
    label: string;
    options: FilterOption[];
    selected: string[];
    onChange: (next: string[]) => void;
}> = ({ label, options, selected, onChange }) => {
    const [open, setOpen] = useState(false);
    const count = selected.length;
    const active = count > 0;
    const toggle = (id: string) =>
        onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    return (
        <Popover open={open} onOpenChange={(_, d) => setOpen(d.open)} positioning="below-start" trapFocus>
            <PopoverTrigger disableButtonEnhancement>
                <button
                    type="button"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "8px 14px",
                        borderRadius: 999,
                        cursor: "pointer",
                        border: active ? "1px solid transparent" : `1px solid ${REFINED.line}`,
                        backgroundColor: active ? REFINED.aubDeep : REFINED.white,
                        color: active ? REFINED.white : REFINED.ink2,
                        fontFamily: REFINED.fontUI,
                        fontSize: "0.9rem",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                    }}
                >
                    {label}
                    {count > 0 ? ` (${count})` : ""}
                    <ChevronDown16Regular />
                </button>
            </PopoverTrigger>
            <PopoverSurface>
                <div style={{ minWidth: 240, maxHeight: 300, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
                    {options.length === 0 ? (
                        <div style={{ color: REFINED.ink3, fontSize: "0.88rem", padding: "8px 4px", fontStyle: "italic" }}>
                            Keine Optionen.
                        </div>
                    ) : (
                        options.map((o) => (
                            <Checkbox key={o.id} label={o.label} checked={selected.includes(o.id)} onChange={() => toggle(o.id)} />
                        ))
                    )}
                </div>
                {count > 0 && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${REFINED.line}`, textAlign: "right" }}>
                        <button
                            type="button"
                            onClick={() => onChange([])}
                            style={{
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                color: REFINED.aub,
                                fontFamily: REFINED.fontUI,
                                fontSize: "0.85rem",
                                fontWeight: 700,
                            }}
                        >
                            Zurücksetzen
                        </button>
                    </div>
                )}
            </PopoverSurface>
        </Popover>
    );
};

const ContactRow: React.FC<{ contact: DashboardContact; border: boolean; onClick: () => void }> = ({ contact, border, onClick }) => (
    <div
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
            }
        }}
        style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 6px",
            borderTop: border ? `1px solid ${REFINED.line}` : "none",
            cursor: "pointer",
        }}
    >
        <span style={{ flexShrink: 0, display: "inline-flex" }}>
            <InitialsAvatar name={contact.displayName} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: "0.95rem", color: REFINED.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {contact.displayName}
            </div>
            {contact.typeLabel && (
                <div style={{ fontSize: "0.83rem", color: REFINED.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {contactTypeLabel(contact.typeLabel)}
                </div>
            )}
        </div>
        <span style={{ flexShrink: 0, fontVariantNumeric: "tabular-nums", color: REFINED.ink3, fontWeight: 700, fontSize: "0.82rem" }}>
            {deDate(contact.lastActivity)}
        </span>
    </div>
);

export default MeineKundenCard;
